import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Sprout, HeartHandshake, ShieldCheck, Clock, LineChart, Leaf, Lock, Key, KeyRound, UserRound, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PROGRAM_CODE = "SCS";
const CODE_KEY = "healthapp.program_access";

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [accessGranted, setAccessGranted] = useState(false);
  const [codeInput, setCodeInput] = useState("");

  // Passwordless email-signin form state
  const [signinName, setSigninName] = useState("");
  const [signinSurname, setSigninSurname] = useState("");
  const [signinEmail, setSigninEmail] = useState("");
  const [signinLoading, setSigninLoading] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    try { setAccessGranted(localStorage.getItem(CODE_KEY) === "granted"); } catch {}
  }, []);

  const submitCode = (e) => {
    if (e) e.preventDefault();
    if (codeInput.trim().toUpperCase() === PROGRAM_CODE) {
      try { localStorage.setItem(CODE_KEY, "granted"); } catch {}
      setAccessGranted(true);
      toast.success("Código correcto. Introduce tus datos para entrar.");
    } else {
      toast.error("Código incorrecto. Solicita el código a tu terapeuta.");
    }
  };

  const scrollToForm = () => {
    if (!accessGranted) {
      toast.error("Primero introduce el código de acceso.");
      return;
    }
    const el = document.querySelector('[data-testid="signin-form-card"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submitSignin = async (e) => {
    if (e) e.preventDefault();
    const name = signinName.trim();
    const surname = signinSurname.trim();
    const email = signinEmail.trim().toLowerCase();
    if (!name || !surname) {
      toast.error("Nombre y apellidos son obligatorios.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Introduce un email válido.");
      return;
    }
    setSigninLoading(true);
    try {
      const { data } = await api.post("/auth/email-signin", { name, surname, email });
      if (data?.is_returning) {
        try {
          sessionStorage.setItem(
            "healthapp.welcome_back",
            JSON.stringify({ firstName: data.first_name || name, lastSession: data.last_session_number ?? null })
          );
        } catch {}
        toast.success(`¡Hola de nuevo, ${data.first_name || name}!`);
      } else {
        toast.success("¡Bienvenido/a! Continuemos tu proceso.");
      }
      window.location.href = "/dashboard";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No pudimos iniciar sesión. Inténtalo de nuevo.";
      toast.error(msg);
    } finally {
      setSigninLoading(false);
    }
  };

  const handleAdminLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-screen bg-[var(--ha-bg)]" data-testid="landing-page">
      {/* Nav */}
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-full bg-[var(--ha-primary)] grid place-items-center shadow-sm">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-extrabold tracking-tight text-[var(--ha-text)] text-lg">MINDHOME HEALTH APP</span>
        </div>
        <Button
          data-testid="nav-login-btn"
          onClick={scrollToForm}
          disabled={!accessGranted}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {accessGranted ? "Entrar al programa" : (<><Lock className="w-4 h-4 mr-2" strokeWidth={2} /> Introduce el código</>)}
        </Button>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-8 pb-20 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--ha-muted)] font-semibold mb-5">
            Terapia Cognitivo-Conductual · Online
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[var(--ha-text)] leading-[1.05]">
            Cuida tu bienestar emocional en <span className="text-[var(--ha-primary)]">8 sesiones</span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[var(--ha-muted)] max-w-xl">
            MINDHOME HEALTH APP es una herramienta de <strong>TCC</strong> basada en evidencia científica,
            diseñada para acompañarte con ejercicios prácticos frente al estrés,
            la ansiedad y la depresión de intensidad leve-moderada.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="hero-start-btn"
              onClick={scrollToForm}
              disabled={!accessGranted}
              className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-12 px-7 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {accessGranted ? "Comenzar ahora" : (<><Lock className="w-4 h-4 mr-2" strokeWidth={2} /> Introduce el código para empezar</>)}
            </Button>
            <a
              href="#ventajas"
              className="rounded-full h-12 px-6 border border-[var(--ha-border)] grid place-items-center text-[var(--ha-text)] hover:bg-white transition-colors"
              data-testid="hero-learn-more"
            >
              Conocer el programa
            </a>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-[var(--ha-muted)]">
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} />Sin efectos adversos</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4" strokeWidth={1.5} />45-60 min/sesión</span>
          </div>

          {/* Access code card */}
          {!accessGranted && (
            <Card className="mt-10 rounded-2xl border-[var(--ha-primary)]/30 bg-white" data-testid="access-code-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[var(--ha-primary)]/10 grid place-items-center shrink-0">
                    <KeyRound className="w-5 h-5 text-[var(--ha-primary)]" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[var(--ha-text)]">Código de acceso al programa</p>
                    <p className="mt-1 text-sm text-[var(--ha-muted)]">Introduce el código que te ha facilitado tu terapeuta para desbloquear el registro.</p>
                    <form onSubmit={submitCode} className="mt-4 flex gap-2">
                      <Input
                        data-testid="access-code-input"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value)}
                        placeholder="Introduce el código"
                        autoComplete="off"
                        className="h-11 rounded-full border-[var(--ha-border)] focus:border-[var(--ha-primary)]"
                      />
                      <Button
                        type="submit"
                        data-testid="access-code-submit"
                        className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-5"
                      >
                        Validar
                      </Button>
                    </form>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {accessGranted && (
            <Card className="mt-8 rounded-2xl border-[var(--ha-primary)]/30 bg-white" data-testid="signin-form-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[var(--ha-primary)]/10 grid place-items-center shrink-0">
                    <ShieldCheck className="w-5 h-5 text-[var(--ha-primary)]" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[var(--ha-text)]">Entra al programa</p>
                    <p className="mt-1 text-sm text-[var(--ha-muted)]">Sin contraseñas: solo tu nombre y un email para guardar tu progreso.</p>
                    <form onSubmit={submitSignin} className="mt-5 space-y-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="relative">
                          <UserRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ha-muted)]" strokeWidth={1.75} />
                          <Input
                            data-testid="signin-name-input"
                            value={signinName}
                            onChange={(e) => setSigninName(e.target.value)}
                            placeholder="Nombre"
                            autoComplete="given-name"
                            className="h-11 pl-9 rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)]"
                            required
                          />
                        </div>
                        <div className="relative">
                          <UserRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ha-muted)]" strokeWidth={1.75} />
                          <Input
                            data-testid="signin-surname-input"
                            value={signinSurname}
                            onChange={(e) => setSigninSurname(e.target.value)}
                            placeholder="Apellidos"
                            autoComplete="family-name"
                            className="h-11 pl-9 rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)]"
                            required
                          />
                        </div>
                      </div>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ha-muted)]" strokeWidth={1.75} />
                        <Input
                          data-testid="signin-email-input"
                          type="email"
                          value={signinEmail}
                          onChange={(e) => setSigninEmail(e.target.value)}
                          placeholder="tu@email.com"
                          autoComplete="email"
                          className="h-11 pl-9 rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)]"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        data-testid="signin-submit-btn"
                        disabled={signinLoading}
                        className="w-full rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 disabled:opacity-60"
                      >
                        {signinLoading ? "Entrando…" : (<>Entrar al programa <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} /></>)}
                      </Button>
                      <p className="text-[11px] text-[var(--ha-muted)] leading-relaxed">
                        Al continuar aceptas que guardemos tu progreso ligado a este email. Si vuelves con el mismo email, retomarás donde lo dejaste.
                      </p>
                    </form>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Card */}
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1594175268654-e60bea6c037c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
            alt="Paisaje sereno"
            className="rounded-3xl w-full h-[420px] object-cover shadow-sm border border-[var(--ha-border)]"
          />
          <div className="absolute -bottom-6 -left-6 hidden sm:block bg-white rounded-2xl border border-[var(--ha-border)] p-5 shadow-md w-64">
            <p className="text-xs uppercase tracking-[0.15em] text-[var(--ha-muted)] font-semibold">Sesión 1</p>
            <p className="mt-2 font-semibold text-[var(--ha-text)]">Primeros pasos + Modelo ABC</p>
            <p className="mt-1 text-sm text-[var(--ha-muted)]">Evaluación inicial, objetivos SMART y práctica guiada.</p>
          </div>
        </div>
      </section>

      {/* Ventajas */}
      <section id="ventajas" className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[var(--ha-text)] mb-10">Ventajas del programa</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Sprout, title: "Basada en evidencia", body: "Enfoque TCC de eficacia probada, sin efectos adversos reportados." },
            { icon: HeartHandshake, title: "Alternativa no farmacológica", body: "Compatible con o sin medicación, siempre bajo tu criterio y el de tu profesional." },
            { icon: Clock, title: "Disponible 24/7", body: "Pausa cuando quieras y retoma justo donde lo dejaste, desde cualquier dispositivo." },
            { icon: LineChart, title: "Monitorea tu progreso", body: "Puntuaciones de PHQ-9, GAD-7 y más para ver avances entre sesiones." },
            { icon: ShieldCheck, title: "Privado y seguro", body: "Tus respuestas se guardan en tu perfil, con acceso solo para ti." },
            { icon: Leaf, title: "Habilidades TCC prácticas", body: "Aprende a reformular pensamientos, fijar objetivos SMART y gestionar tu día a día." },
          ].map(({ icon: Icon, title, body }, i) => (
            <Card key={i} className="border-[var(--ha-border)] rounded-2xl shadow-sm bg-white">
              <CardContent className="p-6">
                <div className="h-10 w-10 rounded-xl bg-[var(--ha-bg)] grid place-items-center border border-[var(--ha-border)]">
                  <Icon className="w-5 h-5 text-[var(--ha-primary)]" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-semibold text-[var(--ha-text)]">{title}</h3>
                <p className="mt-2 text-sm text-[var(--ha-muted)] leading-relaxed">{body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <footer className="border-t border-[var(--ha-border)] py-8 text-center text-sm text-[var(--ha-muted)]">
        <p>© MINDHOME HEALTH APP · Cuidado emocional basado en TCC</p>
        <button
          data-testid="admin-entry-btn"
          onClick={handleAdminLogin}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-[var(--ha-muted)] hover:text-[var(--ha-primary)] transition-colors"
        >
          <Key className="w-3 h-3" strokeWidth={2} /> Acceso administrador
        </button>
      </footer>
    </main>
  );
}
